/* =====================================================================
   WEAPONS — поведение оружия. Спавнит снаряды / наносит AoE-урон.
   Урон по врагам идёт через Game.hitEnemy (там же смерть/дроп/партиклы).
   ===================================================================== */

import { CONFIG } from './config';
import { weaponDef, angleTo, rand, TAU, dist2 } from './utils';
import { Game } from './game';
import { Audio2 } from './audio';

export const Weapons: any = {
  update(player: any, dt: number) {
    for (const w of player.weapons) {
      const def = weaponDef(w.key);
      const lvl = w.level - 1;
      if (def.kind === 'orbit') { this._orbit(player, w, def, lvl, dt); continue; }
      if (def.kind === 'beam')  { this._beam(player, w, def, lvl, dt); continue; }
      w.timer -= dt;
      if (w.timer <= 0) {
        // ОВЕРКЛОК сужает кулдаун оружия (стак × firePerStack); Перегрев (опора захвата) тоже ускоряет залпы
        w.timer += def.cooldown[lvl] * (player.cooldownMult || 1) * (player._snareCdMul || 1) * this._ocFire(w);
        if (def.kind === 'bolt') this._bolt(player, w, def, lvl);
        else if (def.kind === 'nova') this._nova(player, w, def, lvl);
        else if (def.kind === 'whip') this._whip(player, w, def, lvl);
        else if (def.kind === 'chain') this._chain(player, w, def, lvl);
        else if (def.kind === 'mine') this._mine(player, w, def, lvl);
        else if (def.kind === 'lantern') this._lantern(player, w, def, lvl);
        else if (def.kind === 'ricochet') this._ricochet(player, w, def, lvl);
      }
    }
  },

  // ОВЕРКЛОК — единый источник формул (чтобы не рассинхронились по местам чтения).
  // ИНВАРИАНТ: firePerStack*maxStacks < 1, иначе _ocFire < 0 → отрицательный кулдаун → залп каждый кадр.
  _ocFire(w: any) { return (w && w.oc) ? 1 - CONFIG.overclock.firePerStack * w.oc : 1; },
  _ocDmg(w: any)  { return (w && w.oc) ? 1 + CONFIG.overclock.dmgPerStack * w.oc : 1; },

  _dmg(player: any, def: any, lvl: number, w?: any) {
    let d = def.dmg[lvl] * player.damageMult * (player._snareDmgMul || 1) * CONFIG.balance.weaponDmg;
    d *= this._ocDmg(w);   // ОВЕРКЛОК (горн-класс B): макснутое оружие бьёт сильнее за стак (цена — свет, см. updatePlayer)
    // БИЛД-ОСЬ ГРАНИЦЫ (опт-ин, реликвии/аномалии-заветы): уникальная сфера «свет» наконец даёт
    // глубину как ось выбора билда. Тёмный билд — урон растёт при НИЗКОМ жаре (риск/мощь малого ядра);
    // светлый — при ШИРОКОМ. Не форсится: без реликвии/завета оба = 0, базовая модель света не тронута.
    // тёмный полюс = реликвия/завет (_loEdge) + синергии оружия (_synLo)
    if (player._loEdge || player._synLo) {
      let lo = (player._loEdge || 0) + (player._synLo || 0);
      // СИНЕРГИЯ D2: в ПЛОТНОЙ своей выжженной территории (где метки перекрываются — ярче светится)
      // тёмный билд бьёт ещё злее. «Контроль пространства»: построй кил-зону и держи её сердце.
      const dens = player._emberDensity || 0;
      if (dens > 1) lo *= 1 + Math.min(dens - 1, CONFIG.emberField.synergyCap) * CONFIG.emberField.loSynergy;
      // РАННЯЯ выплата (OGD): floor даёт долю бонуса сразу за коммит, остаток масштабируется глубиной нырка
      const lf = CONFIG.light.loFloor || 0;
      d *= 1 + lo * (lf + (1 - lf) * (1 - player.light / CONFIG.light.max));
    }
    // светлый полюс = реликвия (_hiEdge) + синергии оружия (_synHi)
    if (player._hiEdge || player._synHi) {
      let hi = (player._hiEdge || 0) + (player._synHi || 0);
      // СИНЕРГИЯ светлого полюса: на ПИКЕ маятника День/Ночь (ярче всего) светлый билд «вспыхивает» —
      // окна burst по ритму. Награда за игру по маятнику (зеркало тёмной синергии D2).
      const peak = Math.max(0, (Game.dayNightMult() - 1) / CONFIG.cycle.swing);   // 0..1, 1 = пик яркости
      if (peak > 0) hi *= 1 + peak * CONFIG.cycle.hiSynergy;
      d *= 1 + hi * (player.light / CONFIG.light.max);
    }
    return d;
  },

  _bolt(player: any, w: any, def: any, lvl: number) {
    // ГОРН-МОДЫ (флаги ставит карта-мод, см. config.forgeMods): чистые стат-трансформы характера Импульса.
    let count = def.count[lvl];
    if (w.fShrapnel) count += 2;                      // Шрапнель: залп шире
    if (w.fArmor) count = Math.max(1, count - 1);     // Бронебой: один тяжёлый стержень
    let pierce = def.pierce;
    if (w.fHotCore) pierce += 1;                       // Раскалённый сердечник: +1 пробой
    if (w.fArmor) pierce += 3;                         // Бронебой: прошивает строй насквозь
    // ЗОНА СВЕТА = ДАЛЬНОСТЬ (только для def.lightGated, т.е. дефолтного «Импульса»): радиус действия =
    // player.light (та же граница, что рисует кольцо жара). Враг во тьме недосягаем — залп не тратится,
    // оружие «ждёт» и бьёт сразу, как цель входит в свет (ты=свет). Прочие bolt-оружия (тёмный
    // Сумеречный клинок с edgeBonus, эволюции) сохраняют прежнюю дальность и стрельбу вслепую.
    const gated = !!def.lightGated;
    const targets = Game.nearestEnemies(player.x, player.y, count, gated ? player.light : 900);
    if (gated && targets.length === 0) { w.timer = 0.05; return; }   // нет освещённых целей — держим наготове
    // Сумеречный клинок: чем меньше радиус света, тем больнее удар (не зависит от цели — считаем раз)
    let dmgv = this._dmg(player, def, lvl, w);
    if (def.edgeBonus) dmgv *= 1 + def.edgeBonus * (1 - player.light / CONFIG.light.max);
    if (w.fHotCore) dmgv *= 1.35;                      // тяжелее
    if (w.fShrapnel) dmgv *= 0.78;                     // мягче на снаряд (ковёр)
    if (w.fArmor) dmgv *= 1.6;                         // девастирующий стержень
    const n = gated ? targets.length : count;
    for (let i = 0; i < n; i++) {
      const t = targets[i];
      let ang;
      if (t) ang = angleTo(player.x, player.y, t.x, t.y);
      else ang = player.lastDir ? Math.atan2(player.lastDir.y, player.lastDir.x) : rand(0, TAU);
      Game.projectiles.spawn((p: any) => {
        p.x = player.x; p.y = player.y;
        p.vx = Math.cos(ang) * def.speed;
        p.vy = Math.sin(ang) * def.speed;
        p.dmg = dmgv;
        p.radius = def.radius;
        p.life = def.life;
        p.pierce = pierce;
        p.color = def.color;
        p.kind = 'bolt';
        p.knockback = def.knockback;
        p.hitIds = null;
        p.hostile = false;          // пул переиспользует объекты от вражеских выстрелов — сбросить
        p.target = t || null;
        p.angle = ang;
        p.split = !!w.fSplit; p.didSplit = false;   // ГЛАГОЛ «Раскол»: при попадании родит осколки (один раз)
      });
    }
    Audio2.shoot();
  },

  _orbit(player: any, w: any, def: any, lvl: number, dt: number) {
    // ГОРН-МОДЫ (флаги ставит карта-мод): стат-трансформы характера Жар-сферы
    const orbitRadius = def.orbitRadius * (w.fOrbCore ? 1.12 : 1);   // Раскалённое кольцо: шире
    let orbR = def.radius;
    if (w.fOrbOne) orbR *= 1.35;                                     // Смерть одним: крупнее сфера
    w.orbAngle = (w.orbAngle + def.orbitSpeed * dt) % TAU;
    let count = def.count[lvl];
    if (w.fOrbSwarm) count += 2;                                     // Рой: больше сфер
    if (w.fOrbOne) count = Math.max(1, count - 1);                  // Смерть одним: на одну меньше
    let dmg = this._dmg(player, def, lvl, w);
    if (w.fOrbCore) dmg *= 1.3;                                      // жарче
    if (w.fOrbSwarm) dmg *= 0.8;                                     // мягче на сферу
    if (w.fOrbOne) dmg *= 1.9;                                       // тяжёлая сфера
    w._nodes = w._nodes || [];
    for (let i = 0; i < count; i++) {
      const a = w.orbAngle + i * TAU / count;
      const ox = player.x + Math.cos(a) * orbitRadius;
      const oy = player.y + Math.sin(a) * orbitRadius;
      w._nodes[i] = { x: ox, y: oy };
      // урон по перекрытию (с индивидуальным кулдауном врага)
      for (const e of Game.enemies.active) {
        if (e.dead) continue;
        const rr = (orbR + e.radius);
        if (dist2(ox, oy, e.x, e.y) <= rr * rr && e.hitCd <= 0) {
          e.hitCd = def.tick * this._ocFire(w);   // ОВЕРКЛОК: чаще касания (непрерывное оружие — ocFire здесь, не в update)
          Game.hitEnemy(e, dmg, def.knockback, ox, oy);
        }
      }
    }
    w._nodeCount = count;
  },

  _nova(player: any, w: any, def: any, lvl: number) {
    const r = def.novaRadius[lvl];
    const dmg = this._dmg(player, def, lvl, w);
    const r2 = r * r;
    for (const e of Game.enemies.active) {
      if (e.dead) continue;
      if (dist2(player.x, player.y, e.x, e.y) <= (r + e.radius) * (r + e.radius)) {
        Game.hitEnemy(e, dmg, def.knockback, player.x, player.y);
      }
    }
    Game.effects.push({ kind: 'nova', x: player.x, y: player.y, r: 0, maxR: r,
                        life: 0.4, maxLife: 0.4, color: def.color });
    // Рассветная вспышка: радиус света вспыхивает до максимума и держится ~2с
    if (def.lightBurst) { player.light = CONFIG.light.max; player._lightFloor = CONFIG.light.max; player._lightFloorT = 2; }
    Game.addShake(CONFIG.feel.shakeOnNova);
    Audio2.nova();
  },

  _whip(player: any, w: any, def: any, lvl: number) {
    // ГОРН-МОДЫ (флаги ставит карта-мод): стат-трансформы характера Хлыста
    let len = def.whipLen[lvl];
    if (w.fWhipHarpoon) len *= 1.5;                 // Гарпун: бьёт дальше
    let wide = def.whipWide;
    if (w.fWhipSweep) wide *= 1.7;                  // Размах: дуга шире
    if (w.fWhipHarpoon) wide *= 0.6;               // Гарпун: узкая полоса
    let kb = def.knockback;
    if (w.fWhipCore) kb *= 1.3;                     // Кованый замах: сильнее отбрасывает
    if (w.fWhipSweep) kb *= 2;                      // Размах: сметает вдвое
    let dmg = this._dmg(player, def, lvl, w);
    if (w.fWhipCore) dmg *= 1.3;                    // тяжелее
    if (w.fWhipSweep) dmg *= 0.85;                 // мягче на цель (размен на ширину)
    if (w.fWhipHarpoon) dmg *= 1.5;               // тяжёлое копьё
    const count = def.count[lvl];
    // радиальный (эволюция) — во все стороны; иначе по движению (+ назад при count>1)
    let dirs;
    if (def.radial) {
      dirs = [];
      for (let i = 0; i < count; i++) dirs.push(i * TAU / count);
    } else {
      dirs = [Math.atan2(player.lastDir.y, player.lastDir.x)];
      if (count > 1) dirs.push(dirs[0] + Math.PI);
    }
    for (const ang of dirs) {
      const dx = Math.cos(ang), dy = Math.sin(ang);
      for (const e of Game.enemies.active) {
        if (e.dead) continue;
        // проекция на ось удара
        const rx = e.x - player.x, ry = e.y - player.y;
        const proj = rx * dx + ry * dy;
        if (proj < 0 || proj > len + e.radius) continue;
        const perp = Math.abs(rx * -dy + ry * dx);
        if (perp > wide + e.radius) continue;
        // ГЛАГОЛ «Захват»: отрицательный knockback ТЯНЕТ врага к игроку (в жар-границу), а не отбрасывает
        Game.hitEnemy(e, dmg, w.fWhipGrasp ? -kb : kb, player.x, player.y);
      }
      Game.effects.push({ kind: 'whip', x: player.x, y: player.y, ang, len, wide,
                          life: 0.18, maxLife: 0.18, color: def.color });
    }
    Audio2.shoot();
  },

  _chain(player: any, w: any, def: any, lvl: number) {
    const dmg = this._dmg(player, def, lvl, w);
    const maxHops = def.count[lvl];
    let cur = Game.nearestEnemy(player.x, player.y, def.firstRange);
    if (!cur) return;
    const hit = new Set();
    const pts = [{ x: player.x, y: player.y }];
    let fx = player.x, fy = player.y;
    for (let h = 0; h < maxHops && cur; h++) {
      hit.add(cur);
      Game.hitEnemy(cur, dmg, def.knockback, fx, fy);
      pts.push({ x: cur.x, y: cur.y });
      fx = cur.x; fy = cur.y;
      let next = null, bd = def.hopRange * def.hopRange;
      for (const e of Game.enemies.active) {
        if (e.dead || hit.has(e)) continue;
        const d = dist2(fx, fy, e.x, e.y);
        if (d < bd) { bd = d; next = e; }
      }
      cur = next;
    }
    Game.effects.push({ kind: 'chain', pts, life: 0.16, maxLife: 0.16, color: def.color });
    Audio2.shoot();
  },

  // Луч-маяк: непрерывный луч(и) в ближайших, урон тиками вдоль линии
  _beam(player: any, w: any, def: any, lvl: number, dt: number) {
    const count = def.count[lvl];
    const len = def.beamLen[lvl];
    const wide = def.beamWide;
    const targets = Game.nearestEnemies(player.x, player.y, count, len + 140);
    w._beams = [];
    for (let i = 0; i < count; i++) {
      const t = targets[i];
      let ang;
      if (t) ang = angleTo(player.x, player.y, t.x, t.y);
      else if (i === 0) ang = player.lastDir ? Math.atan2(player.lastDir.y, player.lastDir.x) : 0;
      else ang = (w._beams[0] ? w._beams[0].ang : 0) + i * 0.6;
      w._beams.push({ ang, len });
    }
    w._beamTick = (w._beamTick || 0) - dt;
    if (w._beamTick > 0) return;
    // Перегрев (опора захвата) учащает тики луча; ОВЕРКЛОК тоже — непрерывное оружие, ocFire здесь, не в update
    w._beamTick += def.tick * (player._snareCdMul || 1) * this._ocFire(w);
    const dmg = this._dmg(player, def, lvl, w);
    for (const b of w._beams) {
      const dx = Math.cos(b.ang), dy = Math.sin(b.ang);
      for (const e of Game.enemies.active) {
        if (e.dead) continue;
        const rx = e.x - player.x, ry = e.y - player.y;
        const proj = rx * dx + ry * dy;
        if (proj < 0 || proj > len + e.radius) continue;
        const perp = Math.abs(rx * -dy + ry * dx);
        if (perp > wide + e.radius) continue;
        Game.hitEnemy(e, dmg, def.knockback, player.x, player.y);
      }
    }
  },

  // Отражённый луч: снаряд, отскакивающий от кромки тьмы (см. Game.updateRicochet)
  _ricochet(player: any, w: any, def: any, lvl: number) {
    const count = def.count[lvl];
    const targets = Game.nearestEnemies(player.x, player.y, count, 720);
    for (let i = 0; i < count; i++) {
      const t = targets[i];
      const ang = t ? angleTo(player.x, player.y, t.x, t.y)
                    : (player.lastDir ? Math.atan2(player.lastDir.y, player.lastDir.x) : rand(0, TAU));
      Game.projectiles.spawn((p: any) => {
        p.x = player.x; p.y = player.y;
        p.vx = Math.cos(ang) * def.speed; p.vy = Math.sin(ang) * def.speed;
        p.dmg = this._dmg(player, def, lvl, w);
        p.radius = def.radius; p.life = def.life; p.color = def.color;
        p.kind = 'ricochet'; p.knockback = def.knockback;
        p.bounces = def.bounces[lvl]; p.bounceGain = def.bounceGain;
        p.hitIds = null; p.angle = ang; p.pierce = 0; p.hostile = false;
      });
    }
    Audio2.shoot();
  },

  // Пульс-фонарь: ставит стоячий источник света — свет-зону (см. Game.updateLantern)
  _lantern(player: any, w: any, def: any, lvl: number) {
    const count = def.count[lvl];
    for (let i = 0; i < count; i++) {
      const a = rand(0, TAU), r = count > 1 ? rand(20, 60) : 0;
      Game.projectiles.spawn((p: any) => {
        p.x = player.x + Math.cos(a) * r; p.y = player.y + Math.sin(a) * r;
        p.vx = 0; p.vy = 0; p.kind = 'lantern';
        p.radius = def.lanternRadius[lvl];
        p.life = def.lanternLife[lvl]; p.maxLife = p.life;
        p.dmg = def.tickDmg[lvl] * player.damageMult * CONFIG.balance.weaponDmg * this._ocDmg(w);   // ОВЕРКЛОК: лампа не зовёт _dmg, применяем урон тут
        p.tick = def.tick; p.tickTimer = def.tick;
        p.slowMul = def.slow; p.color = def.color;
        p.hostile = false; p.hitIds = null;
      });
    }
    Audio2.nova();
  },

  _mine(player: any, w: any, def: any, lvl: number) {
    const count = def.count[lvl];
    const dmg = this._dmg(player, def, lvl, w);
    for (let i = 0; i < count; i++) {
      const a = rand(0, TAU), r = count > 1 ? rand(24, 66) : 0;
      Game.projectiles.spawn((p: any) => {
        p.x = player.x + Math.cos(a) * r; p.y = player.y + Math.sin(a) * r;
        p.vx = 0; p.vy = 0; p.kind = 'mine';
        p.dmg = dmg; p.color = def.color; p.radius = def.mineRadius;
        p.trigger = def.mineTrigger; p.life = def.mineLife;
        p.knockback = def.knockback; p.hostile = false; p.hitIds = null;
      });
    }
  },
};
